import { DISCLOSURE_BUTTON_CLASS, TOP_LEVEL_BUTTON_CLASS, SUBMENU_BUTTON_CLASS, ARROW_SVG } from './Constants';
import { getJQuery, getLinkText, isShowcaseWrapper } from './Utils';

let $: JQueryStatic;

// =============================================================================
// ARIA Attribute Migration
// =============================================================================

/**
 * Migrate ARIA attributes from a link to a disclosure button.
 * Removes aria-haspopup, aria-controls, aria-expanded from the link.
 *
 * For non-showcase dropdowns, aria-labelledby is set on the submenu <ul> so
 * AT can announce the list's accessible name. Showcase wrappers are
 * presentational (role="presentation" — see reconcileSubmenuRoles below);
 * adding a global ARIA property other than aria-hidden would invalidate the
 * presentation role per ARIA 1.2, so we skip aria-labelledby on them.
 */
const migrateAriaAttributes = ($link: JQuery, $button: JQuery, $sub: JQuery): void => {
    const ariaControls = $link.attr('aria-controls') ?? '';
    const ariaExpanded = $link.attr('aria-expanded') ?? 'false';

    $button.attr({
        'aria-controls': ariaControls,
        'aria-expanded': ariaExpanded,
    });

    $link.removeAttr('aria-haspopup');
    $link.removeAttr('aria-controls');
    $link.removeAttr('aria-expanded');

    const buttonId = $button.attr('id');
    if (buttonId && !isShowcaseWrapper($sub)) {
        $sub.attr('aria-labelledby', buttonId);
    }
};

// =============================================================================
// Button Creation
// =============================================================================

/**
 * Create a disclosure button for a top-level nav item.
 * The button is visually hidden until focused, then shows a downward chevron.
 */
const createTopLevelButton = (linkText: string, submenuId: string): JQuery<HTMLElement> => {
    const buttonId = `disclosure-${submenuId}`;
    const $button = $(`<button type="button"></button>`);

    $button.attr({
        id: buttonId,
        class: `${DISCLOSURE_BUTTON_CLASS} ${TOP_LEVEL_BUTTON_CLASS}`,
        'aria-label': linkText,
    });

    $button.append(ARROW_SVG);

    return $button;
};

/**
 * Create a disclosure button for a submenu item.
 * Wraps the existing SVG arrow; always visible.
 */
const createSubmenuButton = (linkText: string, submenuId: string, $svg: JQuery<HTMLElement>): JQuery<HTMLElement> => {
    const buttonId = `disclosure-${submenuId}`;
    const $button = $(`<button type="button"></button>`);

    $button.attr({
        id: buttonId,
        class: `${DISCLOSURE_BUTTON_CLASS} ${SUBMENU_BUTTON_CLASS}`,
        'aria-label': linkText,
    });

    $button.append($svg);

    return $button;
};

// =============================================================================
// Button Injection — Top-Level Items
// =============================================================================

/**
 * Inject disclosure buttons alongside top-level navigation links.
 *
 * For each top-level <li> with a submenu, inserts a visually-hidden button
 * between the <a> and the <ul>. ARIA attributes are migrated from the link
 * to the button.
 */
const injectTopLevelDisclosureButtons = (menuSelectors: readonly string[]): void => {
    menuSelectors.forEach((selector) => {
        $(selector)
            .find('> li.top-nav')
            .each(function () {
                const $li = $(this);
                const $link = $li.children('a').first();
                const $sub = $li.children('ul.dropdown-menu').first();

                if (!$link.length || !$sub.length) {
                    return;
                }

                if (!$link.attr('aria-haspopup')) {
                    return;
                }

                const submenuId = $sub.attr('id') ?? '';
                if (!submenuId) {
                    return;
                }

                const linkText = getLinkText($link);
                const $button = createTopLevelButton(linkText, submenuId);

                migrateAriaAttributes($link, $button, $sub);
                $link.after($button);
            });
    });
};

// =============================================================================
// Button Injection — Submenu Items
// =============================================================================

/**
 * Wrap existing SVG arrows in submenu items with disclosure buttons.
 *
 * For each submenu <li> that has a nested <ul>, extracts the SVG arrow
 * from the link, wraps it in a button, and migrates ARIA attributes.
 */
const wrapSubmenuArrowsInButtons = (menuSelectors: readonly string[]): void => {
    menuSelectors.forEach((selector) => {
        $(selector)
            .find('ul.dropdown-menu li')
            .each(function () {
                const $li = $(this);
                const $link = $li.children('a').first();
                const $sub = $li.children('ul.dropdown-menu').first();

                if (!$link.length || !$sub.length) {
                    return;
                }

                if (!$link.attr('aria-haspopup')) {
                    return;
                }

                const submenuId = $sub.attr('id') ?? '';
                if (!submenuId) {
                    return;
                }

                const $svg = $link.find('svg').first() as unknown as JQuery<HTMLElement>;
                if (!$svg.length) {
                    return;
                }

                $svg.detach();

                const $wrapper = $link.children('div').first();
                if ($wrapper.length) {
                    const $span = $wrapper.find('span').first();
                    if ($span.length) {
                        $span.detach();
                        $wrapper.replaceWith($span);
                    }
                }

                const linkText = getLinkText($link);
                const $button = createSubmenuButton(linkText, submenuId, $svg);

                migrateAriaAttributes($link, $button, $sub);
                $link.after($button);
            });
    });
};

// =============================================================================
// ARIA Cleanup — Remove Incorrect Roles
// =============================================================================

/**
 * Reconcile the `role` attribute SmartMenus sets on every submenu <ul>.
 *
 * SmartMenus places `role="group"` on every submenu <ul>, which clobbers any
 * role authored in markup. Two cases:
 *
 * - Standard dropdowns: the W3C disclosure navigation pattern does not use
 *   `role="group"`. The dropdown is a plain <ul> whose visibility is
 *   controlled by the disclosure button's aria-expanded state, so we strip
 *   the role entirely.
 * - Showcase mega-menus (`.ss-dropdown-menu-with-showcase`): the outer <ul>
 *   and its two `<li>` children are layout containers (two-column
 *   image/text panel + submenu links), not a semantic list. We re-apply
 *   `role="presentation"` so screen readers don't announce a spurious
 *   two-item list around the showcase content (WCAG 2.2 SC 1.3.1). The
 *   `role="none"` on the child <li>s is authored in
 *   webroot/views/showcase-menu-panel.php and not touched here.
 *
 * Additionally, the inner <ul> inside `.ss-submenu-links` (the real nav list)
 * also receives `role="group"`, `aria-labelledby`, and `aria-expanded` from
 * SmartMenus. It is not a `.dropdown-menu` so the loop above does not reach it.
 * We strip those three attributes here; `aria-hidden` is intentionally kept
 * because it is the AT-visibility mechanism for show/hide.
 */
const reconcileSubmenuRoles = (menuSelectors: readonly string[]): void => {
    menuSelectors.forEach((selector) => {
        $(selector)
            .find('ul.dropdown-menu')
            .each(function () {
                const $ul = $(this);
                if (isShowcaseWrapper($ul)) {
                    $ul.attr('role', 'presentation');
                    // Per ARIA 1.2, presentation is invalidated by any non-default
                    // global ARIA property except aria-hidden. SmartMenus and our
                    // disclosure migration may attach aria-labelledby / aria-expanded
                    // to every submenu <ul>; strip them so AT honors presentation
                    // on the showcase wrapper.
                    $ul.removeAttr('aria-labelledby');
                    $ul.removeAttr('aria-expanded');

                    // Clean up the inner nav list that SmartMenus also decorates.
                    $ul.find('.ss-submenu-links > ul').each(function () {
                        $(this).removeAttr('role');
                        $(this).removeAttr('aria-labelledby');
                        $(this).removeAttr('aria-expanded');
                    });
                } else {
                    $ul.removeAttr('role');
                }
            });
    });
};

// =============================================================================
// ARIA Cleanup — Translate Trigger
// =============================================================================

/**
 * Remove aria-haspopup from the Translate trigger link.
 *
 * SmartMenus stamps aria-haspopup="true" on every <a> that precedes a
 * submenu <ul>. The Translate item (li.translate > a) has a submenu, so it
 * receives the attribute — but it is not a page-nav dropdown and has no
 * disclosure button, so aria-haspopup is misleading for AT. Strip it while
 * leaving aria-controls and aria-expanded intact (they are valid for the
 * show/hide mechanism SmartMenus manages).
 */
const cleanTranslateTrigger = (menuSelectors: readonly string[]): void => {
    menuSelectors.forEach((selector) => {
        $(selector).find('li.translate > a').removeAttr('aria-haspopup');
    });
};

// =============================================================================
// Public Setup
// =============================================================================

/**
 * Set up all disclosure buttons: inject top-level and submenu buttons,
 * migrate ARIA attributes, and remove incorrect roles. Also strips the
 * SmartMenus-injected aria-haspopup from the Translate trigger.
 */
export const setupDisclosureButtons = (menuSelectors: readonly string[]): void => {
    $ = getJQuery();
    injectTopLevelDisclosureButtons(menuSelectors);
    wrapSubmenuArrowsInButtons(menuSelectors);
    reconcileSubmenuRoles(menuSelectors);
    cleanTranslateTrigger(menuSelectors);
};
